/**
 * v4.11.0 — Fleet Detail Drawer.
 *
 * In-page side panel. Does NOT navigate away from Fleet Ops. Pulls the
 * single-card detail aggregation from the orchestration endpoint and
 * renders the operator-relevant summary. Mutation actions stay on the
 * existing /cards routes — this drawer just deep-links to them.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ExternalLink, Calendar, Building2, MapPin, User, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import ScheduleSnapshot from "./ScheduleSnapshot";
import CardKpiPanel from "./CardKpiPanel";
import { parseUtc } from '../../lib/datetime';

const Section = ({ icon: Icon, title, children, testId }) => (
    <div data-testid={testId} className="space-y-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400">
            {Icon ? <Icon size={12} strokeWidth={1.75} /> : null}
            {title}
        </div>
        <div className="text-sm text-slate-200">{children}</div>
    </div>
);

export default function FleetDetailDrawer({ cardId, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cardId) {
            setData(null);
            return;
        }
        setLoading(true);
        api.get(`/card-fleet/card/${cardId}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [cardId]);

    if (!cardId) return null;
    const card = data?.card;

    return (
        <>
            <div
                className="fixed inset-0 z-40"
                style={{ background: "rgba(2, 6, 23, 0.55)" }}
                onClick={onClose}
                aria-hidden
            />
            <aside
                role="dialog"
                aria-modal="true"
                data-testid="fleet-detail-drawer"
                data-insight-id="fleet.drawer.card-detail"
                data-insight-label="Card detail drawer"
                className="fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] overflow-y-auto border-l"
                style={{
                    background: "rgb(15, 23, 42)",
                    borderColor: "rgba(148, 163, 184, 0.12)",
                }}
            >
                <div
                    className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b"
                    style={{
                        background: "rgb(15, 23, 42)",
                        borderColor: "rgba(148, 163, 184, 0.12)",
                    }}
                >
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wider text-slate-400">
                            {t("card_fleet.drawer.eyebrow")}
                        </p>
                        <h2 className="text-lg font-semibold text-slate-100 truncate">
                            {card ? `#${card.card_id || card.name || ""}` : t("common.loading")}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="fleet-drawer-close"
                        aria-label={t("common.close")}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
                    >
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {loading || !card ? (
                        <div className="text-sm text-slate-400">{t("common.loading")}</div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2">
                                <span className="inline-flex items-center gap-1.5 text-xs text-slate-200">
                                    <span
                                        className="inline-block w-2 h-2 rounded-full"
                                        style={{
                                            background: card.status === "active" ? "#10b981" :
                                                card.status === "paused" ? "#f59e0b" : "#94a3b8",
                                        }}
                                    />
                                    {t(`card_fleet.status.${card.status}`, { defaultValue: card.status })}
                                </span>
                                {card.review_shield_enabled && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
                                          style={{ background: "rgba(16,185,129,0.12)", color: "#10b981" }}>
                                        <ShieldCheck size={11} strokeWidth={1.9} />
                                        {t("card_fleet.mode.review_shield")}
                                    </span>
                                )}
                            </div>

                            <Section icon={MapPin} title={t("card_fleet.drawer.location")}
                                     testId="drawer-section-location">
                                <div>{card.location_name || "—"}</div>
                                {card.organization_name && (
                                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                                        <Building2 size={11} /> {card.organization_name}
                                    </div>
                                )}
                            </Section>

                            <Section icon={Calendar} title={t("card_fleet.drawer.routing")}
                                     testId="drawer-section-routing">
                                <div className="mb-1">
                                    {t("card_fleet.drawer.currently_routing", {
                                        platform: t(`card_fleet.platforms.${card.current_destination}`,
                                            { defaultValue: card.current_destination || "—" }),
                                    })}
                                </div>
                                <ScheduleSnapshot
                                    schedule={card.schedule}
                                    fallbackPlatform={card.platform}
                                />
                            </Section>

                            <Section icon={User} title={t("card_fleet.drawer.assigned_to")}
                                     testId="drawer-section-assignment">
                                {card.employee_name ? (
                                    <>
                                        <div>{card.employee_name}</div>
                                        {card.employee_code && (
                                            <div className="text-xs text-slate-500">
                                                {t("employees.employee_id_field.label")}: {card.employee_code}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-slate-500 italic">
                                        {t("card_fleet.unassigned_employee")}
                                    </span>
                                )}
                            </Section>

                            <Section title={t("card_fleet.drawer.activity")} testId="drawer-section-activity">
                                <div className="flex gap-4">
                                    <div>
                                        <p className="text-[11px] text-slate-400">{t("card_fleet.kpi.taps_today")}</p>
                                        <p className="text-xl font-semibold text-slate-100">
                                            {(card.taps_today || 0).toLocaleString()}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-slate-400">{t("card_fleet.drawer.total_taps")}</p>
                                        <p className="text-xl font-semibold text-slate-100">
                                            {(card.tap_count || 0).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </Section>

                            {data?.recent_taps?.length > 0 && (
                                <Section title={t("card_fleet.drawer.recent_taps")} testId="drawer-section-recent">
                                    <ul className="space-y-1">
                                        {data.recent_taps.slice(0, 5).map((tap, i) => (
                                            <li key={i} className="flex items-center justify-between text-xs">
                                                <span className="text-slate-300">
                                                    {t(`card_fleet.platforms.${tap.platform}`,
                                                        { defaultValue: tap.platform || "—" })}
                                                </span>
                                                <span className="text-slate-500">
                                                    {tap.timestamp ? parseUtc(tap.timestamp).toLocaleString() : "—"}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </Section>
                            )}

                            {/* v4.11.1 — Premium KPI donuts (this card vs location) */}
                            <div
                                className="pt-4 border-t"
                                style={{ borderColor: "rgba(148, 163, 184, 0.10)" }}
                                data-testid="drawer-kpi-strip"
                            >
                                <CardKpiPanel cardId={card.id} />
                            </div>

                            <div className="pt-3 border-t" style={{ borderColor: "rgba(148, 163, 184, 0.12)" }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        // v4.11.2 — Stash Fleet Ops state so the operator
                                        // returns here automatically after editing.
                                        try {
                                            sessionStorage.setItem(
                                                "yot.fleet_ops.return_state",
                                                JSON.stringify({
                                                    drawer_card_id: card.id,
                                                    drawer_card_card_id: card.card_id,
                                                    scroll_y: window.scrollY,
                                                    stamped_at: Date.now(),
                                                })
                                            );
                                        } catch { /* sessionStorage unavailable — proceed */ }
                                        window.location.href =
                                            `/cards?card_id=${encodeURIComponent(card.card_id || card.id)}` +
                                            `&from=fleet-ops&return_to=fleet-ops`;
                                    }}
                                    data-testid="drawer-link-card-settings"
                                    data-insight-id="fleet.drawer.card-settings-link"
                                    data-insight-label="Open card settings"
                                    className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline"
                                >
                                    {t("card_fleet.drawer.open_card_settings")}
                                    <ExternalLink size={12} strokeWidth={1.75} />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
